import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  CircularProgress,
  Fade,
  Grow,
  Paper,
  Typography,
  useTheme,
  IconButton,
} from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/**
 * A generic virtualized list component that handles:
 * - Prepend/append items without scroll position jumps
 * - Infinite scroll in both directions
 * - Pull to refresh
 * - Dynamic item heights
 *
 * @param {Object} props
 * @param {Array} props.items - The list items to render
 * @param {Function} props.renderItem - Function to render each item
 * @param {Function} props.loadMore - Function to load more items when scrolling
 * @param {Function} props.loadPrevious - Function to load previous items when scrolling up
 * @param {Function} props.onRefresh - Function to call when pull-to-refresh is triggered
 * @param {boolean} props.hasMore - Whether there are more items to load
 * @param {boolean} props.hasPrevious - Whether there are previous items to load
 * @param {boolean} props.isLoading - Whether the list is currently loading
 * @param {number} props.overscan - Number of items to render outside the visible area
 * @param {string} props.className - Additional CSS class name
 * @param {Object} props.style - Additional styles
 * @param {number} props.itemHeight - Height of each item
 * @param {string} props.containerHeight - Height of the container
 * @param {boolean} props.scrollTopTransition - Whether to animate scroll position changes
 * @param {string} props.newItemsText - Text to show for new items (default: "new items above")
 * @param {Function} props.isItemUnread - Function to determine if an item is unread
 * @param {Function} props.onAppend - Function to call when new items are appended
 * @param {Function} props.onPrepend - Function to call when new items are prepended
 */
const VirtualList = ({
  items,
  renderItem,
  loadMore,
  hasMore = false,
  isLoading = false,
  overscan = 5,
  className,
  style,
  itemHeight = 100,
  containerHeight = "100%",
  scrollTopTransition = true,
  newItemsText = "new items above",
  isItemUnread = () => false,
  onAppend = () => {},
  onPrepend = () => {},
}) => {
  const theme = useTheme();
  const parentRef = useRef(null);
  const lastScrollTop = useRef(0);
  const [actualItemsLength, setActualItemsLength] = useState(items.length);
  const [newItems, setNewItems] = useState(new Set());
  const [newItemsCount, setNewItemsCount] = useState(0);
  const isNearBottom = useRef(false);
  const [loadingHeight, setLoadingHeight] = useState(0);
  const [lastItemsLength, setLastItemsLength] = useState(items.length);
  const [lastFirstItem, setLastFirstItem] = useState(items[0]?.id);
  const [isUpdatingScroll, setIsUpdatingScroll] = useState(false);
  const lastUpdateTime = useRef(0);
  const lastItemsRef = useRef(items);

  const virtualizerConfig = useMemo(
    () => ({
      count: items.length,
      getScrollElement: () => parentRef.current,
      estimateSize: () => itemHeight,
      overscan,
    }),
    [items.length, itemHeight, overscan]
  );

  const rowVirtualizer = useVirtualizer(virtualizerConfig);

  // Handle scroll events for infinite scroll
  const handleScroll = useCallback(
    (e) => {
      if (!parentRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = e.target;
      lastScrollTop.current = scrollTop;

      // Calculate distance from bottom
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isCurrentlyNearBottom = distanceFromBottom < 100;

      // Only trigger loadMore if we've just entered the "near bottom" state
      if (
        isCurrentlyNearBottom &&
        !isNearBottom.current &&
        hasMore &&
        !isLoading
      ) {
        isNearBottom.current = true;
        loadMore();
      } else if (!isCurrentlyNearBottom) {
        isNearBottom.current = false;
      }
    },
    [hasMore, loadMore, isLoading]
  );

  // Track new items when they're added
  useEffect(() => {
    console.log("Items changed:", {
      currentFirst: items[0]?.id,
      lastFirst: lastFirstItem,
      currentLength: items.length,
      previousLength: actualItemsLength,
      scrollTop: parentRef.current?.scrollTop,
    });

    // Check if this is a complete list replacement
    const isListReplacement =
      items.length <= actualItemsLength &&
      items[0]?.id !== lastFirstItem &&
      !items.some((item) => item.id === lastFirstItem);

    // Prevent duplicate updates within 100ms
    const now = Date.now();
    if (now - lastUpdateTime.current < 100 && !isListReplacement) {
      return;
    }

    if (
      (items[0]?.id !== lastFirstItem || isListReplacement) &&
      parentRef.current &&
      !isUpdatingScroll
    ) {
      // Items were prepended
      const newItems = [];
      let i = 0;

      if (isListReplacement) {
        // For list replacement, we need to handle it differently based on scroll position
        if (parentRef.current.scrollTop === 0) {
          // If we're at the top, all items are new
          newItems.push(...items);
        } else {
          // If we're not at the top, find items that are newer than our current view
          const lastItems = new Set(
            lastItemsRef.current.map((item) => item.id)
          );
          const currentFirstItem = items[0]?.id;

          // Find the first item that exists in both lists
          let commonItemIndex = items.findIndex((item) =>
            lastItems.has(item.id)
          );
          if (commonItemIndex === -1) {
            // If no common items, all items are new
            newItems.push(...items);
          } else {
            // Add items before the common item
            for (i = 0; i < commonItemIndex; i++) {
              newItems.push(items[i]);
            }
          }
        }
      } else {
        // Normal prepend case
        const lastFirstItemIndex = items.findIndex(
          (item) => item.id === lastFirstItem
        );
        if (lastFirstItemIndex === -1) {
          // If we're at the top of the list (scrollTop is 0), treat all items as new
          if (parentRef.current.scrollTop === 0) {
            newItems.push(...items);
          } else {
            // If we're not at the top, only treat items before the previous length as new
            for (i = 0; i < Math.min(items.length, actualItemsLength); i++) {
              newItems.push(items[i]);
            }
          }
        } else {
          // Normal case: find all items before lastFirstItem
          while (i < lastFirstItemIndex) {
            newItems.push(items[i]);
            i++;
          }
        }
      }

      const diff = newItems.length;
      if (diff > 0) {
        setIsUpdatingScroll(true);
        lastUpdateTime.current = now;

        // Calculate new scroll position
        const currentScrollTop = parentRef.current.scrollTop;
        const newScrollTop = currentScrollTop + diff * itemHeight;

        console.log("Updating scroll for prepend:", {
          diff,
          currentScroll: currentScrollTop,
          newScroll: newScrollTop,
          newItems: newItems.map((n) => n.id),
          isListReplacement,
          isAtTop: currentScrollTop === 0,
          currentFirstItem: items[0]?.id,
          lastFirstItem,
        });

        // Update scroll position
        if (currentScrollTop > 0) {
          parentRef.current.scrollTop = newScrollTop;
        }

        // Call onPrepend with only the truly new items
        onPrepend(newItems);

        // Reset updating flag after a short delay
        setTimeout(() => {
          setIsUpdatingScroll(false);
        }, 50);
      }
    } else if (items.length > actualItemsLength) {
      const newItemIndices = new Set();
      for (let i = actualItemsLength; i < items.length; i++) {
        newItemIndices.add(i);
      }
      setNewItems(newItemIndices);

      // Call onAppend with the new items
      const newItems = items.slice(actualItemsLength);
      onAppend(newItems);

      // Clear the new items after animation
      setTimeout(() => {
        setNewItems(new Set());
      }, 300);
    } else if (items.length < actualItemsLength && parentRef.current) {
      // Items were removed
      const removedItemIndices = new Set();
      for (let i = items.length; i < actualItemsLength; i++) {
        removedItemIndices.add(i);
      }
      setNewItems(removedItemIndices);

      // Clear the new items after animation
      setTimeout(() => {
        setNewItems(new Set());
      }, 300);
    }

    setLastFirstItem(items[0]?.id);
    setActualItemsLength(items.length);
    lastItemsRef.current = items;
  }, [items, actualItemsLength, itemHeight, onAppend, onPrepend]);

  // Track new items above current view
  useEffect(() => {
    if (items.length > 0 && parentRef.current) {
      const firstVisibleIndex =
        Math.floor(parentRef.current.scrollTop / itemHeight) || 0;
      const newItemsAbove = items.filter(
        (item, index) => index < firstVisibleIndex && isItemUnread(item)
      ).length;
      setNewItemsCount(newItemsAbove);
    }
  }, [items.length, itemHeight, parentRef.current?.scrollTop, isItemUnread]);

  // Initialize refs when component mounts
  useEffect(() => {
    if (parentRef.current) {
      lastScrollTop.current = parentRef.current.scrollTop;
    }
  }, []);

  // Update loading state
  useEffect(() => {
    if (isLoading) {
      setLoadingHeight(100);
    } else {
      setLoadingHeight(0);
    }
  }, [isLoading]);

  const scrollToTop = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  return (
    <Box
      ref={parentRef}
      className={className}
      style={{
        height: containerHeight,
        overflow: "auto",
        position: "relative",
        zIndex: 1,
        ...style,
      }}
      onScroll={handleScroll}
    >
      {/* New Items Banner */}
      <Fade in={newItemsCount > 0}>
        <Paper
          elevation={2}
          onClick={scrollToTop}
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            p: 1.5,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderRadius: 1,
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            marginBottom: "-40px",
            "&:hover": {
              bgcolor: theme.palette.primary.dark,
              transform: "translateY(-1px)",
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <Typography
            variant="body2"
            align="center"
            sx={{
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
            {newItemsCount} {newItemsText}
          </Typography>
        </Paper>
      </Fade>

      <Box
        style={{
          height: `${rowVirtualizer.getTotalSize() + loadingHeight}px`,
          width: "100%",
          position: "relative",
          zIndex: 0,
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          const isNew =
            virtualRow.index === 0 && newItems.has(virtualRow.index);

          return (
            <Box
              key={item.id}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: itemHeight,
                transform: `translateY(${virtualRow.start}px)`,
                willChange: "transform",
                zIndex: 0,
              }}
            >
              <Grow in={true} timeout={300}>
                {renderItem(item, virtualRow.index)}
              </Grow>
            </Box>
          );
        })}
      </Box>

      {/* Loading indicator */}
      {isLoading && (
        <Box
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: loadingHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: theme.palette.background.paper,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}

      {/* Scroll to Top Button */}
      <Fade in={parentRef.current?.scrollTop > 100}>
        <IconButton
          onClick={scrollToTop}
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            zIndex: 10,
            bgcolor: theme.palette.background.paper,
            color: theme.palette.primary.main,
            boxShadow: theme.shadows[4],
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              transform: "translateY(-2px)",
              boxShadow: theme.shadows[6],
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      </Fade>
    </Box>
  );
};

export default VirtualList;
